import { z } from 'zod'
import { calendarDate } from './dates'
import { itemSourceSchema } from './item-source'
import { timeOffSchema } from './timeoff'

/**
 * Contracts of hugo's planner and heads-ups. The planner (packages/insights) fills the working
 * days from now with the dated tasks and I owe items, earliest due first, and says when each one
 * completes; heads-ups name who should hear from the user because of it. Both are deterministic:
 * dates and minutes come from code, never from the AI.
 */

/** Time needed, in minutes, when the user estimated it. Without one hugo assumes a default by type. */
export const ESTIMATE_MINUTES_MIN = 5
export const ESTIMATE_MINUTES_MAX = 2400
export const estimateMinutesSchema = z
  .number()
  .int()
  .min(ESTIMATE_MINUTES_MIN)
  .max(ESTIMATE_MINUTES_MAX)

/**
 * overdue: the due time has passed. at_risk: the work completes after the due day, or more than
 * a year from today. on_track: it completes on or before the due day.
 */
export const planStatuses = ['on_track', 'at_risk', 'overdue'] as const
export const planStatusSchema = z.enum(planStatuses)
export type PlanStatus = z.output<typeof planStatusSchema>

/**
 * When an item's work gets done: dated tasks and promises, and undated ones the user planned on
 * the board. Only `items.list`, `item.get` and the board fill it.
 */
export const itemPlanSchema = z.object({
  status: planStatusSchema,
  /** The local date of `dueAt` in the user's time zone; null for an undated item on the board. */
  dueOn: calendarDate.nullable(),
  /** The local day the work completes; null when that is more than a year from today. */
  projectedOn: calendarDate.nullable(),
  /**
   * At risk or overdue: the earliest new date that holds once promised (items with earlier dates
   * come first after the move). Later than `projectedOn` when such items exist. Null when on
   * track or beyond a year.
   */
  promiseOn: calendarDate.nullable().default(null),
  /** The minutes the planner counted for the item. */
  minutes: z.number().int().min(0),
  /** true: `minutes` is the user's estimate; false: hugo's default for the item's type. */
  estimated: z.boolean(),
  /** The first time off overlapping today to `promiseOn`/`projectedOn` (or containing `dueOn`). */
  timeOff: timeOffSchema.pick({ startsOn: true, endsOn: true }).nullable(),
  /** The due day is a day off. */
  dueInTimeOff: z.boolean(),
  /**
   * Minutes meetings (connected calendars) took from the planned hours from today to
   * `projectedOn`: only on days they leave less than the hours a day. 0 beyond a year.
   */
  meetingMinutes: z.number().int().min(0).default(0),
  /**
   * Minutes out-of-office time in connected calendars took from the planned hours until
   * `projectedOn`, beyond what meetings took. 0 beyond a year.
   */
  outOfOfficeMinutes: z.number().int().min(0).default(0),
  /** Minutes of its work hugo plans for today. */
  todayMinutes: z.number().int().min(0).default(0),
  /** The first day hugo plans time for it; null when not within a year. */
  startOn: calendarDate.nullable().default(null),
})
export type ItemPlan = z.output<typeof itemPlanSchema>

/**
 * owe_late: a promise to someone will be late (at risk or overdue). waiting_away: someone the
 * user waits on should know about time off coming up. waiting_followup: the follow-up date for
 * someone the user waits on has come and they have not written since it was set: a reminder.
 */
export const headsUpKinds = ['owe_late', 'waiting_away', 'waiting_followup'] as const
export const headsUpKindSchema = z.enum(headsUpKinds)
export type HeadsUpKind = z.output<typeof headsUpKindSchema>

/**
 * reply: a reply in the item's Gmail thread, through the Outbox (approval as always). copy: a
 * text the user copies and sends themselves.
 */
export const headsUpChannels = ['reply', 'copy'] as const
export const headsUpChannelSchema = z.enum(headsUpChannels)
export type HeadsUpChannel = z.output<typeof headsUpChannelSchema>

export const headsUpLanguages = ['en', 'de'] as const
export const headsUpLanguageSchema = z.enum(headsUpLanguages)
export type HeadsUpLanguage = z.output<typeof headsUpLanguageSchema>

export const HEADS_UP_EPISODE_MAX = 200
export const HEADS_UP_TEXT_MAX = 2000

/**
 * After a reminder (sent, or "I reminded them"), the next follow-up is this many working days
 * later: if they still have not written by then, hugo suggests another reminder.
 */
export const REMINDER_INTERVAL_WORKING_DAYS = 3

/**
 * What a heads-up is about, e.g. `owe:<itemId>:<dueOn>`, `waiting:<itemId>:<timeOffId>` or
 * `followup:<itemId>:<followUpOn>`: a new due date, a new time off or a new follow-up date is a
 * new episode. Once dismissed, or once any Outbox entry exists for it (discarded ones included;
 * not the ones hugo withdrew), an episode is not suggested again.
 */
export const headsUpEpisodeSchema = z.string().min(1).max(HEADS_UP_EPISODE_MAX)

/** Who should hear from the user, why, and the text to tell them (EN or DE). */
export const headsUpSchema = z.object({
  episode: headsUpEpisodeSchema,
  kind: headsUpKindSchema,
  itemId: z.uuid(),
  title: z.string(),
  /** The person to tell: the item's counterpart. */
  person: z.string().nullable(),
  source: itemSourceSchema,
  channel: headsUpChannelSchema,
  /** Due day (owe_late) or follow-up day (waiting_away, waiting_followup). */
  dueOn: calendarDate.nullable(),
  /**
   * owe_late: the date the work can be promised for (the planner's projectedOn).
   * waiting_followup: the next follow-up day once reminded (REMINDER_INTERVAL_WORKING_DAYS on).
   */
  newDate: calendarDate.nullable(),
  /** waiting_away: the last working day before the time off. */
  askBy: calendarDate.nullable(),
  /** waiting_away: the first working day after the time off. */
  backOn: calendarDate.nullable(),
  timeOff: timeOffSchema.pick({ id: true, startsOn: true, endsOn: true, label: true }).nullable(),
  language: headsUpLanguageSchema,
  text: z.string().max(HEADS_UP_TEXT_MAX),
})
export type HeadsUp = z.output<typeof headsUpSchema>
